import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Quote, ChevronLeft, ChevronRight, Star } from "lucide-react";
import { Reveal, SectionLabel } from "@/components/motion";
import { TESTIMONIALS } from "@/data/content";

export default function TestimonialsCarousel() {
  const [idx, setIdx] = useState(0);
  const [paused, setPaused] = useState(false);

  useEffect(() => {
    if (paused) return;
    const t = setInterval(() => setIdx((i) => (i + 1) % TESTIMONIALS.length), 6000);
    return () => clearInterval(t);
  }, [paused, idx]);

  const t = TESTIMONIALS[idx];

  return (
    <section className="px-5 sm:px-8 py-20 lg:py-28 bg-[#F8FAFC]" data-testid="testimonials-section">
      <div className="max-w-4xl mx-auto text-center">
        <Reveal>
          <SectionLabel>Client Voices</SectionLabel>
          <h2 className="font-display font-bold text-3xl sm:text-4xl lg:text-[44px] tracking-tight text-[#01193D]">
            Trusted by teams who expect more
          </h2>
        </Reveal>
        <Reveal delay={0.15} className="mt-12">
          <div
            className="relative bg-white rounded-3xl soft-shadow border border-slate-100 px-8 sm:px-14 py-12 min-h-[300px] flex flex-col justify-center overflow-hidden"
            onMouseEnter={() => setPaused(true)}
            onMouseLeave={() => setPaused(false)}
          >
            <Quote className="absolute -top-4 -left-2 text-[#CAF2F9]" size={140} strokeWidth={1} aria-hidden="true" />
            <AnimatePresence mode="wait">
              <motion.div
                key={idx}
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -12 }}
                transition={{ duration: 0.4 }}
                className="relative"
              >
                <div className="flex justify-center gap-1 mb-5" data-testid="testimonial-rating">
                  {[...Array(t.rating)].map((_, i) => (
                    <Star key={i} size={17} className="fill-[#09B2ED] text-[#09B2ED]" />
                  ))}
                </div>
                <p className="text-lg lg:text-xl text-[#01193D] leading-relaxed font-medium">"{t.quote}"</p>
                <div className="flex items-center justify-center gap-3 mt-7">
                  <span className="w-11 h-11 rounded-full bg-gradient-to-br from-[#2563EB] to-[#09B2ED] text-white font-display font-bold text-sm flex items-center justify-center">
                    {t.name.split(" ").map((w) => w[0]).join("")}
                  </span>
                  <div className="text-left">
                    <p className="font-display font-semibold text-[#01193D] leading-tight">{t.name}</p>
                    <p className="text-xs text-slate-500">{t.role}, <span className="text-[#2563EB] font-medium">{t.company}</span></p>
                  </div>
                </div>
              </motion.div>
            </AnimatePresence>
          </div>
          <div className="flex items-center justify-center gap-4 mt-7">
            <button
              data-testid="testimonial-prev-button"
              onClick={() => setIdx((idx - 1 + TESTIMONIALS.length) % TESTIMONIALS.length)}
              className="w-11 h-11 rounded-full border border-slate-200 flex items-center justify-center text-[#01193D] hover:bg-[#01193D] hover:text-white transition-colors"
              aria-label="Previous testimonial"
            >
              <ChevronLeft size={18} />
            </button>
            <div className="flex items-center gap-2.5">
              {TESTIMONIALS.map((_, i) => (
                <button key={i} onClick={() => setIdx(i)} aria-label={`Testimonial ${i + 1}`} className="relative h-2 rounded-full overflow-hidden bg-slate-200" style={{ width: i === idx ? 36 : 8 }}>
                  {i === idx && !paused && (
                    <span key={idx} className="testi-progress absolute inset-y-0 left-0 bg-gradient-to-r from-[#2563EB] to-[#09B2ED] rounded-full" />
                  )}
                  {i === idx && paused && <span className="absolute inset-0 bg-gradient-to-r from-[#2563EB] to-[#09B2ED] rounded-full" />}
                </button>
              ))}
            </div>
            <button
              data-testid="testimonial-next-button"
              onClick={() => setIdx((idx + 1) % TESTIMONIALS.length)}
              className="w-11 h-11 rounded-full border border-slate-200 flex items-center justify-center text-[#01193D] hover:bg-[#01193D] hover:text-white transition-colors"
              aria-label="Next testimonial"
            >
              <ChevronRight size={18} />
            </button>
          </div>
        </Reveal>
      </div>
    </section>
  );
}
